import * as React from "react";

/**
 * Single module: debounced search input + substring filter over arbitrary lists.
 * Substring match is O(n) per debounced query; debouncing limits how often that runs.
 */

const DEFAULT_DEBOUNCE_MS = 300;

export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = React.useState(value);

  React.useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);

  return debounced;
}

export function normalizeSearchTerm(term: string): string {
  return term.trim().toLowerCase();
}

function textMatchesNormalizedSearch(haystack: string, normalizedNeedle: string): boolean {
  if (!normalizedNeedle) {
    return true;
  }
  return haystack.toLowerCase().includes(normalizedNeedle);
}

/** Pure filter: `searchTerm` + `items` + text extractor. Safe to use outside React. */
export function filterItemsBySearchTerm<T>(
  items: readonly T[],
  searchTerm: string,
  getSearchableText: (item: T) => string
): T[] {
  const needle = normalizeSearchTerm(searchTerm);
  if (!needle) {
    return items as T[];
  }
  return items.filter((item) => textMatchesNormalizedSearch(getSearchableText(item), needle));
}

export type UseDebouncedSearchFilterOptions<T> = {
  debounceMs?: number;
  getSearchableText: (item: T) => string;
};

export type UseDebouncedSearchFilterResult<T> = {
  searchInput: string;
  setSearchInput: React.Dispatch<React.SetStateAction<string>>;
  debouncedSearchTerm: string;
  filteredItems: T[];
};

export function useDebouncedSearchFilter<T>(
  items: readonly T[],
  options: UseDebouncedSearchFilterOptions<T>
): UseDebouncedSearchFilterResult<T> {
  const { debounceMs = DEFAULT_DEBOUNCE_MS, getSearchableText } = options;
  const [searchInput, setSearchInput] = React.useState("");
  const debouncedSearchTerm = useDebouncedValue(searchInput, debounceMs);

  const filteredItems = React.useMemo(
    () => filterItemsBySearchTerm(items, debouncedSearchTerm, getSearchableText),
    [items, debouncedSearchTerm, getSearchableText]
  );

  return {
    searchInput,
    setSearchInput,
    debouncedSearchTerm,
    filteredItems,
  };
}
